import {
  Box,
  Card,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";
import React from "react";
import { IStayDetail } from "../../../../../redux/leads/leads.types";
import {
  CustomCurrencyFieldV3,
  CustomSelect,
} from "../../../../../components/formsComponents";
import { CustomDatePicker } from "../../../../../components/formsComponents/CustomDatePicker";
import { MuiNumberField } from "../../../../../components/formsComponents/MuiNumberField";
import { formatPrice } from "../../../../../utils/formatPrice";
import { DiscountValueField } from "./DiscountValueField";

interface IRoomOption {
  room_uuid?: string | null;
  room_name: string;
}

/** Fields of a stay row that must be filled before saving (discount is optional). */
export type StayRowRequiredField =
  | "room_name"
  | "check_in"
  | "check_out"
  | "rooms"
  | "price";

interface IQuotationStayDetailsProps {
  rows: IStayDetail[];
  rooms: IRoomOption[];
  total: number;
  isMobile?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, key: keyof IStayDetail, value: any) => void;
  /** When provided, the returned fields of each row are highlighted as required. */
  getMissingFields?: (row: IStayDetail) => StayRowRequiredField[];
}

/** Quotation-level stay (room) details — shared by every event of the lead. */
export const QuotationStayDetails: React.FC<IQuotationStayDetailsProps> = ({
  rows,
  rooms,
  total,
  isMobile = false,
  readOnly = false,
  disabled = false,
  onAdd,
  onRemove,
  onChange,
  getMissingFields,
}) => {
  const isMissing = (row: IStayDetail, field: StayRowRequiredField) =>
    Boolean(getMissingFields?.(row).includes(field));

  const renderRoomSelect = (row: IStayDetail, index: number) => (
    <CustomSelect
      size="small"
      fullWidth
      value={row.room_name}
      disabled={disabled}
      error={isMissing(row, "room_name")}
      onChange={(e) => onChange(index, "room_name", e.target.value)}
      displayEmpty
    >
      <MenuItem value="" disabled>
        Select Room
      </MenuItem>
      {rooms.map((room) => (
        <MenuItem key={room.room_uuid || room.room_name} value={room.room_name}>
          {room.room_name}
        </MenuItem>
      ))}
    </CustomSelect>
  );

  /** Shown as soon as the dates are entered, not only after a save attempt. */
  const isCheckOutBeforeCheckIn = (row: IStayDetail) =>
    Boolean(
      row.check_in &&
        row.check_out &&
        moment(row.check_out).isBefore(moment(row.check_in), "day"),
    );

  const renderCheckIn = (row: IStayDetail, index: number) => (
    <CustomDatePicker
      value={row.check_in}
      placeholder="Check In"
      fullWidth
      disabled={disabled}
      errorMessage={isMissing(row, "check_in") ? "Required" : undefined}
      maxDate={row.check_out ? moment(row.check_out) : undefined}
      onChange={(val) => onChange(index, "check_in", val)}
    />
  );

  const renderCheckOut = (row: IStayDetail, index: number) => (
    <CustomDatePicker
      value={row.check_out}
      placeholder="Check Out"
      fullWidth
      disabled={disabled}
      errorMessage={
        isCheckOutBeforeCheckIn(row)
          ? "Cannot be before check in"
          : isMissing(row, "check_out")
            ? row.check_out
              ? "Must be after check in"
              : "Required"
            : undefined
      }
      minDate={row.check_in ? moment(row.check_in) : undefined}
      onChange={(val) => onChange(index, "check_out", val)}
    />
  );

  const renderDiscount = (row: IStayDetail, index: number) => (
    <DiscountValueField
      name={`stay_discount_${index}`}
      mode={row.discount_mode === "amount" ? "amount" : "percentage"}
      value={
        row.discount !== undefined && row.discount !== null
          ? String(row.discount)
          : ""
      }
      disabled={disabled}
      onModeChange={(m) => onChange(index, "discount_mode", m)}
      onChange={(newValue) => onChange(index, "discount", newValue)}
    />
  );

  const renderTotal = (row: IStayDetail) => (
    <>
      <Typography variant="body2" fontWeight={600}>
        {formatPrice(row.total)}
      </Typography>
      {Number(row.gst_amount || 0) > 0 && (
        <Typography variant="caption" color="text.secondary">
          incl. GST {formatPrice(row.gst_amount)}
        </Typography>
      )}
    </>
  );

  const addRoomButton = !readOnly && (
    <Box
      sx={{
        mt: 1.5,
        border: "1px dashed",
        borderColor: "text.primary",
        borderRadius: 1.5,
        p: 1,
        textAlign: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        "&:hover": disabled
          ? undefined
          : { borderColor: "primary.main", backgroundColor: "action.hover" },
      }}
      onClick={disabled ? undefined : onAdd}
    >
      <Typography fontWeight={600} fontSize="0.85rem">
        + Add Room
      </Typography>
    </Box>
  );

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Stay Details
      </Typography>

      {isMobile ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {rows.map((row, index) => (
            <Card
              key={index}
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "none" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>{renderRoomSelect(row, index)}</Box>
                {!readOnly && (
                  <IconButton
                    size="small"
                    color="error"
                    disabled={disabled}
                    onClick={() => onRemove(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Check In
                  </Typography>
                  {renderCheckIn(row, index)}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Check Out
                  </Typography>
                  {renderCheckOut(row, index)}
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    # Rooms
                  </Typography>
                  <MuiNumberField
                    name={`stay_rooms_mobile_${index}`}
                    label=""
                    value={row.rooms}
                    error={isMissing(row, "rooms") ? "Required" : undefined}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange(index, "rooms", Number(e.target.value))
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    # Nights
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {row.nights}
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="caption" color="text.secondary">
                    Rack Rate
                  </Typography>
                  <CustomCurrencyFieldV3
                    id={`stay_price_mobile_${index}`}
                    value={String(row.price)}
                    error={isMissing(row, "price")}
                    helperText={isMissing(row, "price") ? "Required" : undefined}
                    disabled={disabled}
                    onChange={(newValue) => onChange(index, "price", newValue)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Discount
                  </Typography>
                  {renderDiscount(row, index)}
                </Grid>
              </Grid>
              <Box
                sx={{
                  mt: 1,
                  pt: 1,
                  borderTop: "1px dashed",
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  Total
                </Typography>
                <Box sx={{ textAlign: "right" }}>{renderTotal(row)}</Box>
              </Box>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer sx={{ overflow: "visible" }}>
          <Table
            size="small"
            sx={{
              tableLayout: "fixed",
              width: "100%",
              "& .MuiTableCell-root": { px: 0.75 },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                {!readOnly && <TableCell sx={{ width: "7%" }}>Actions</TableCell>}
                <TableCell sx={{ width: "15%" }}>Room Name</TableCell>
                <TableCell sx={{ width: "14%" }}>Check In</TableCell>
                <TableCell sx={{ width: "14%" }}>Check Out</TableCell>
                <TableCell sx={{ width: "8%" }}># Rooms</TableCell>
                <TableCell sx={{ width: "7%", textAlign: "center" }}>
                  # Nights
                </TableCell>
                <TableCell sx={{ width: "11%", textAlign: "right" }}>
                  Rack Rate
                </TableCell>
                <TableCell sx={{ width: "13%" }}>Discount</TableCell>
                <TableCell sx={{ width: "11%", textAlign: "right" }}>
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{ "& .MuiTableCell-root": { verticalAlign: "top" } }}
                >
                  {!readOnly && (
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.25 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={disabled}
                          onClick={onAdd}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={disabled}
                          onClick={() => onRemove(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>{renderRoomSelect(row, index)}</TableCell>
                  <TableCell>{renderCheckIn(row, index)}</TableCell>
                  <TableCell>{renderCheckOut(row, index)}</TableCell>
                  <TableCell>
                    <MuiNumberField
                      name={`stay_rooms_${index}`}
                      label=""
                      value={row.rooms}
                      error={isMissing(row, "rooms") ? "Required" : undefined}
                      disabled={disabled}
                      onChange={(e) =>
                        onChange(index, "rooms", Number(e.target.value))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, textAlign: "center" }}
                    >
                      {row.nights}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <CustomCurrencyFieldV3
                      id={`stay_price_${index}`}
                      value={String(row.price)}
                      error={isMissing(row, "price")}
                      helperText={isMissing(row, "price") ? "Required" : undefined}
                      disabled={disabled}
                      onChange={(newValue) => onChange(index, "price", newValue)}
                    />
                  </TableCell>
                  <TableCell>{renderDiscount(row, index)}</TableCell>
                  <TableCell sx={{ textAlign: "right", pt: "14px !important" }}>
                    {renderTotal(row)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {rows.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1.5,
            mt: 1,
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            Stay Details Total
          </Typography>
          <Box
            sx={{
              backgroundColor: "#e3f2fd",
              borderRadius: 1,
              px: 2,
              py: 0.75,
              minWidth: 110,
              textAlign: "right",
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              {formatPrice(total)}
            </Typography>
          </Box>
        </Box>
      )}

      {addRoomButton}
    </Paper>
  );
};
